import rrwebPlayer from 'rrweb-player';
import React, {useEffect, useRef, useState} from 'react';

import {getProjectId} from 'common/report/util';
import {SelectReact} from 'common/components/select';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {appApiUrl} from 'common/report/queries/utils';
import {GenerateSignedReplayParamsResponse} from 'app_api/projects/replays/types';
import {apiClient} from 'common/util/api-client';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';

import './index.styl';
import styles from './index.module.styl';
import {RecordingMetadata} from './types';

const EVENT_FILE_BATCH_SIZE = 20;

type RecordingPlayerProps = {
  replay: RecordingMetadata;
  onStartReplay: (replayId: string) => void;
  onFinishReplay: (replayId: string) => void;
  updateCurrentReplayEvent: (eventTime: number) => void;
  currentRecordingEventSelectedTime: number;
  children?: React.ReactNode;
  copyButton?: React.ReactNode;
};

enum RecordingSpeed {
  Normal = 1,
  Double = 2,
  Quadruple = 4,
  Octuple = 8,
  Dectuple = 10,
}

enum TimeDisplaySetting {
  Hours = `hours`,
  Minutes = `minutes`,
  Seconds = `seconds`,
}

type SignedReplayParams = GenerateSignedReplayParamsResponse['results'];

async function fetchSignedReplayParams(replayId: string): Promise<SignedReplayParams> {
  const url = appApiUrl(`replays/sign`, {projectId: getProjectId()});
  return apiClient.post<SignedReplayParams>(url, {replay_id: replayId});
}

function buildSignedReplayUrl(path: string, signedReplayParams: SignedReplayParams): string {
  return `${signedReplayParams.url}${path}?${signedReplayParams.query_string}`;
}

export function RecordingPlayer(props: RecordingPlayerProps) {
  const playerElementRef = useRef<HTMLDivElement>(null);
  const rrwebPlayerRef = useRef<rrwebPlayer | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const [loadingRecording, setLoadingRecording] = useState<boolean>(true);
  const [showingEmptyState, setShowingEmptyState] = useState<boolean>(false);

  const [recordingPaused, setRecordingPaused] = useState<boolean>(false);
  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [recordingFinished, setRecordingFinished] = useState<boolean>(false);
  const [recordingSpeed, setRecordingSpeed] = useState<RecordingSpeed>(RecordingSpeed.Normal);
  const [recordingSkippingInactive, setRecordingSkippingInactive] = useState<boolean>(false);

  const abortController = useRef<AbortController>(new AbortController());

  useEffect(() => {
    return () => {
      if (rrwebPlayerRef.current) {
        rrwebPlayerRef.current.getReplayer().destroy();
        abortController.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (props.replay?.replayId) {
      showPlayer(props.replay.replayId);
    }
  }, [props.replay?.replayId]);

  useEffect(() => {
    const listener = () => {
      // This check is needed because of an rrweb bug where the finish event is emitted even though the recording was paused
      if (!recordingPaused) {
        setRecordingFinished(true);
        props.onFinishReplay(props.replay.replayId);
      }
    };

    rrwebPlayerRef.current?.getReplayer().on(`finish`, listener);

    return () => {
      rrwebPlayerRef.current?.getReplayer().off(`finish`, listener);
    };
  }, [props.onFinishReplay, rrwebPlayerRef.current]);

  useEffect(() => {
    goToEventTime();
  }, [props.currentRecordingEventSelectedTime]);

  async function fetchOneJsonEventFile(batchId: number, signedReplayParams: SignedReplayParams) {
    const signal = abortController.current.signal;

    const fn = `${String(batchId).padStart(4, `0`)}.json`;
    try {
      const fileUrl = buildSignedReplayUrl(fn, signedReplayParams);
      const res = await fetch(fileUrl, {signal});

      if (res.status === 404) {
        return null;
      }
      try {
        return await res.json();
      } catch (err) {
        console.error(`failed to parse recording json`);
        return null;
      }
    } catch (err) {
      if (err.name === `AbortError`) {
        return null;
      }
      throw err;
    }
  }

  async function showPlayer(replayId: string) {
    playerElementRef.current.innerHTML = ``;
    if (rrwebPlayerRef.current) {
      rrwebPlayerRef.current.getReplayer().destroy();
    }
    rrwebPlayerRef.current = null;

    setLoadingRecording(true);
    setShowingEmptyState(false);
    props.onStartReplay(replayId);

    // Fetch all recording files and stitch together into a single events
    // array for rrweb player. We don't know how many files there are for
    // a given recording, so we keep fetching in batches until we get a 404
    // (represented as a null return value from fetchOneJsonEventFile).
    let batchStartIdx = 0;
    let foundEnd = false;

    const signedReplayParams = await fetchSignedReplayParams(replayId);

    while (!foundEnd) {
      const fileFetches = Array(EVENT_FILE_BATCH_SIZE)
        .fill(null)
        .map((_, fi) => fetchOneJsonEventFile(batchStartIdx + fi, signedReplayParams));
      let eventBatches = await Promise.all(fileFetches);
      if (eventBatches.some((events) => events === null)) {
        foundEnd = true;
        eventBatches = eventBatches.filter(Boolean);
      }
      const events = eventBatches.flat();
      batchStartIdx += EVENT_FILE_BATCH_SIZE;

      if (!rrwebPlayerRef.current) {
        setLoadingRecording(false);

        if (events.length > 0) {
          rrwebPlayerRef.current = new rrwebPlayer({
            target: playerElementRef.current,
            props: {
              events,
              height: 400,
              width: 800,
              triggerFocus: false,
              showController: false,
            },
          });
          setRecordingFinished(false);
          setRecordingPaused(false);
          setRecordingSkippingInactive(false);
          rrwebPlayerRef.current.setSpeed(recordingSpeed);
          rrwebPlayerRef.current.addEventListener(`ui-update-current-time`, (event) => {
            setRecordingProgress(event.payload);
            props.updateCurrentReplayEvent(event.payload / 1000 + props.replay.time);
          });
          rrwebPlayerRef.current.addEventListener(`ui-update-player-state`, () => {
            setLoadingRecording(false);
          });
          rrwebPlayerRef.current.addEventListener(`skip-start`, () => {
            setRecordingSkippingInactive(true);
            setRecordingSpeed(RecordingSpeed.Dectuple);
            addToast({
              type: ToastType.INFO,
              message: `Skipping inactive period.`,
            });
          });
          rrwebPlayerRef.current.addEventListener(`skip-end`, () => {
            setRecordingSkippingInactive(false);
            setRecordingSpeed(RecordingSpeed.Normal);
            addToast({
              type: ToastType.INFO,
              message: `Finished skipping inactive period.`,
            });
          });
          goToEventTime();
        } else {
          setShowingEmptyState(true);
        }
      } else {
        for (const event of events) {
          rrwebPlayerRef.current.addEvent(event);
        }
      }

      const metadata = rrwebPlayerRef.current.getMetaData();
      setRecordingDuration(metadata.totalTime);
    }
  }

  function goToEventTime() {
    if (props.currentRecordingEventSelectedTime && rrwebPlayerRef.current) {
      const timeFromRecordingStart = Math.max(props.currentRecordingEventSelectedTime - props.replay.time, 0);
      rrwebPlayerRef.current.goto(Math.min(timeFromRecordingStart * 1000, recordingDuration));
    }
  }

  function toggleRecording() {
    if (rrwebPlayerRef.current) {
      if (recordingFinished) {
        setRecordingFinished(false);
        setRecordingPaused(false);
        setRecordingProgress(0);
        rrwebPlayerRef.current.getReplayer().play(0);
      } else {
        setRecordingPaused(!recordingPaused);
        rrwebPlayerRef.current.toggle();
      }
    }
  }

  function updatePlayerSpeed(newSpeed: RecordingSpeed) {
    if (rrwebPlayerRef.current) {
      setRecordingSpeed(newSpeed);
      setRecordingSkippingInactive(false);
      rrwebPlayerRef.current.setSpeed(newSpeed);
    }
  }

  function updatePlayerProgress(event) {
    if (rrwebPlayerRef.current && progressBarRef.current && !loadingRecording) {
      setLoadingRecording(true);
      setRecordingSkippingInactive(false);
      const boundingBox = progressBarRef.current.getBoundingClientRect();
      const xDelta = event.clientX - boundingBox.left;
      const percentage = xDelta / boundingBox.width;
      const timeOffset = percentage * recordingDuration;
      setRecordingProgress(timeOffset);
      // This operation can be quite slow in some cases, which is why we want to set the loading state and not block on it completing
      setTimeout(() => rrwebPlayerRef.current.goto(timeOffset), 0);
    }
  }

  function formatRecordingProgressTimeBar() {
    let progress = new Date(recordingProgress);
    const duration = new Date(recordingDuration);

    if (progress > duration) {
      progress = duration;
    }

    let timeDisplaySetting = TimeDisplaySetting.Hours;
    if (duration.getUTCHours() === 0) {
      if (duration.getUTCMinutes() === 0) {
        timeDisplaySetting = TimeDisplaySetting.Seconds;
      } else {
        timeDisplaySetting = TimeDisplaySetting.Minutes;
      }
    }
    return `${formatRecordingProgressTime(progress, timeDisplaySetting)} / ${formatRecordingProgressTime(
      duration,
      timeDisplaySetting,
    )}`;
  }

  function formatRecordingProgressTime(date: Date, setting: TimeDisplaySetting) {
    if (setting === TimeDisplaySetting.Hours) {
      return date.toISOString().substring(11, 19);
    } else if (setting === TimeDisplaySetting.Minutes) {
      return date.toISOString().substring(14, 19);
    } else {
      return date.toISOString().substring(15, 19);
    }
  }

  if (rrwebPlayerRef.current) {
    const metadata = rrwebPlayerRef.current.getMetaData();
    if (metadata.totalTime !== recordingDuration) {
      setRecordingDuration(metadata.totalTime);
    }
  }

  return (
    <div className={styles.recordingContent}>
      <div
        className={styles.playerTarget}
        ref={playerElementRef}
        role={`region`}
        style={loadingRecording ? {visibility: `hidden`} : {}}
      />
      <div className={styles.playerControls}>
        {rrwebPlayerRef.current && (
          <div className={styles.playerControlsSpace}>
            <div className={styles.playerControlsProgressBarArea}>
              <div
                className={styles.playerControlsProgressBar}
                onClick={(event) => updatePlayerProgress(event)}
                ref={progressBarRef}
              >
                <div
                  className={styles.playerControlsProgressBarFilled}
                  style={{
                    width:
                      recordingDuration !== 0
                        ? `${Math.min(100 * (recordingProgress / recordingDuration), 100)}%`
                        : `0%`,
                    backgroundColor: recordingSkippingInactive ? `var(--gray4)` : `var(--blue1)`,
                  }}
                >
                  <div
                    className={styles.playerControlsProgressBarIndicator}
                    style={recordingSkippingInactive ? {backgroundColor: `var(--gray4)`} : {}}
                  />
                </div>
              </div>
            </div>
            <div className={styles.playerControlsArea}>
              <div className={styles.playerControlsAreaLeft}>
                <ButtonReact
                  className={styles.primaryButton}
                  icon={recordingFinished ? `restart` : recordingPaused ? `play` : `pause`}
                  onClick={() => toggleRecording()}
                  theme={ButtonTheme.Primary}
                />
                <SelectReact
                  className={styles.playerControlsAreaSelect}
                  sections={[
                    {
                      items: [
                        {value: RecordingSpeed.Normal, label: `1x Speed`},
                        {value: RecordingSpeed.Double, label: `2x Speed`},
                        {value: RecordingSpeed.Quadruple, label: `4x Speed`},
                        {value: RecordingSpeed.Octuple, label: `8x Speed`},
                        {value: RecordingSpeed.Dectuple, label: `10x Speed`},
                      ],
                    },
                  ]}
                  selectedOption={`${recordingSpeed}x Speed`}
                  menuHeight={173}
                  onChange={(event) => updatePlayerSpeed(event.detail.item.value)}
                />
              </div>
              <div>{formatRecordingProgressTimeBar()}</div>
              <div className={styles.playerControlsAreaRight}>
                {/* TODO: Bring in the full screen button once the functionality has been implemented */}
                {/* <ButtonReact className={styles.expandButton} icon={`expand`}/> */}
                {props.copyButton}
              </div>
            </div>
          </div>
        )}
      </div>
      {props.replay && (
        <>
          {loadingRecording && (
            <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Large} role={`progressbar`} />
          )}
          {!loadingRecording && showingEmptyState && (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateTitle}>No replay found</div>
              <div className={styles.emptyStateSubtitle}>
                This replay may have been deleted or is still being processed. See{` `}
                <a href={`https://docs.mixpanel.com/docs/features/session-replay#faq`} target={`_blank`}>
                  our docs
                </a>
                {` `}
                for further details.
              </div>
            </div>
          )}
        </>
      )}
      {props.children}
    </div>
  );
}
